import type { BaseComponentProps } from '@json-render/react';
import type { GeoPermissibleObjects } from 'd3-geo';
import { geoEqualEarth, geoMercator, geoNaturalEarth1, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { type D3ZoomEvent, zoom as d3Zoom, zoomIdentity } from 'd3-zoom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import max from 'lodash/max';
import min from 'lodash/min';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// topojson-client converts TopoJSON to GeoJSON for rendering
import { feature } from 'topojson-client';
// world-atlas ships pre-built TopoJSON from Natural Earth. The 110m resolution
// (~40KB gzipped) is sufficient for country-level choropleths.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — type declaration in src/world-atlas.d.ts
import worldTopology from 'world-atlas/countries-110m.json';
import type { z } from 'zod';

import type { ShippingMapProps as ShippingMapSchema } from './catalog';
import {
  COLOR_INTERPOLATORS,
  DEFAULT_FLOW_COLORS,
  MAP_COLORS,
  VESSEL_STATUS_COLORS,
} from './shipping-map/constants';
import {
  ChoroplethLayer,
  FlowLayer,
  MarkerLayer,
  RouteLayer,
  type TooltipState,
  VesselLayer,
} from './shipping-map/layers';
import { PieMarkerLayer } from './shipping-map/PieMarkerLayer';
import type { ChoroplethItem, FlowItem, MarkerItem } from './shipping-map/useMapData';
import { useMapData } from './shipping-map/useMapData';

type ShippingMapProps = z.infer<typeof ShippingMapSchema>;

// Default map dimensions — width is overridden by container measurement
const DEFAULT_WIDTH = 960;
const DEFAULT_HEIGHT = 500;

const PROJECTIONS = {
  naturalEarth: geoNaturalEarth1,
  mercator: geoMercator,
  equalEarth: geoEqualEarth,
} as const;

/**
 * ShippingMap — generative UI component that renders a world map with three
 * optional layers: flow arcs, vessel markers, and choropleth country fills.
 *
 * Uses D3 for geographic math (projections, paths, great circle interpolation)
 * but renders all DOM as React JSX for compatibility with the json-render system.
 */
export const ShippingMap = ({ props }: BaseComponentProps<ShippingMapProps>) => {
  const { flows, vessels, routes, markers, pieMarkers, choropleth } = useMapData(props);
  const height = props.height ?? DEFAULT_HEIGHT;

  // -------------------------------------------------------------------------
  // Container width measurement via ResizeObserver
  // -------------------------------------------------------------------------
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWidth(entry.contentRect.width || DEFAULT_WIDTH);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // -------------------------------------------------------------------------
  // TopoJSON → GeoJSON features (one-time conversion)
  // -------------------------------------------------------------------------
  const countries = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const topo = worldTopology as any;
    const fc = feature(topo, topo.objects.countries);
    // feature() returns a FeatureCollection; extract the array
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ('features' in fc ? fc.features : [fc]) as any[];
  }, []);

  // -------------------------------------------------------------------------
  // Projection + path generator — recalculated when container resizes
  // -------------------------------------------------------------------------
  const projection = useMemo(() => {
    const factory = PROJECTIONS[props.projection ?? 'naturalEarth'] ?? geoNaturalEarth1;
    const proj = factory();

    // When center + scale are provided, use explicit positioning instead of fitSize.
    // This lets the chatbot zoom into a specific region (e.g. [49, 26] scale 1400
    // for the Middle East) rather than always showing the full world.
    if (props.center && props.scale) {
      proj
        .center(props.center as [number, number])
        .scale(props.scale)
        .translate([width / 2, height / 2]);
    } else {
      proj.fitSize([width, height], {
        type: 'FeatureCollection',
        features: countries,
      });
    }
    return proj;
  }, [props.projection, props.center, props.scale, width, height, countries]);

  const pathGenerator = useMemo(() => geoPath(projection), [projection]);

  // -------------------------------------------------------------------------
  // Zoom / pan via d3-zoom — stores transform in React state
  // -------------------------------------------------------------------------
  const [transform, setTransform] = useState(zoomIdentity);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // d3-zoom listens for wheel/drag events and produces zoom transforms.
    // Constrain to 1x–8x; translateExtent keeps the map roughly in view.
    const zoomBehavior = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 8])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .on('zoom', (event: D3ZoomEvent<SVGSVGElement, unknown>) => {
        setTransform(event.transform);
      });

    select(svg).call(zoomBehavior);

    return () => {
      // Remove zoom listeners on cleanup
      select(svg).on('.zoom', null);
    };
  }, [width, height]);

  // -------------------------------------------------------------------------
  // Tooltip state — shared across all layers
  // -------------------------------------------------------------------------
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const handleTooltip = useCallback(
    (update: TooltipState | null | ((prev: TooltipState | null) => TooltipState | null)) => {
      if (isFunction(update)) {
        setTooltip(update);
      } else {
        setTooltip(update);
      }
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Resolve cartographic palette — dark mode detected from the <html> class
  // -------------------------------------------------------------------------
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  const palette = isDark ? MAP_COLORS.dark : MAP_COLORS.light;

  // -------------------------------------------------------------------------
  // Check if any layer has data
  // -------------------------------------------------------------------------
  const hasFlows = !isEmpty(flows);
  const hasRoutes = !isEmpty(routes);
  const hasVessels = !isEmpty(vessels);
  const hasMarkers = !isEmpty(markers);
  const hasPieMarkers = !isEmpty(pieMarkers);
  const hasChoropleth = !isEmpty(choropleth);
  const hasAnyData = hasFlows || hasRoutes || hasVessels || hasMarkers || hasPieMarkers || hasChoropleth;

  if (!hasAnyData) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        No map data provided
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {props.title && (
        <div className="text-sm font-semibold text-foreground mb-1">{props.title}</div>
      )}
      <div ref={containerRef} className="relative w-full overflow-hidden rounded-lg">
        <svg
          ref={svgRef}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="cursor-grab active:cursor-grabbing"
        >
          {/* Ocean background — cartographic blue */}
          <rect width={width} height={height} fill={palette.ocean} />

          <g transform={transform.toString()}>
            {/* Base country outlines — always rendered even without choropleth */}
            {!hasChoropleth && (
              <g className="base-countries">
                {map(countries, (feat, i) => (
                  // A handful of small unrecognized territories in this topology have
                  // id: undefined - index disambiguates so React doesn't collide their keys.
                  <path
                    key={`${feat.id}-${i}`}
                    d={pathGenerator(feat as GeoPermissibleObjects) ?? ''}
                    fill={palette.land}
                    stroke={palette.border}
                    strokeWidth={0.5}
                  />
                ))}
              </g>
            )}

            {/* Choropleth layer — country fills */}
            {hasChoropleth && (
              <ChoroplethLayer
                features={countries}
                path={pathGenerator}
                projection={projection}
                data={choropleth}
                colorScheme={props.colorScheme ?? 'blues'}
                landColor={palette.land}
                borderColor={palette.border}
                onTooltip={handleTooltip}
              />
            )}

            {/* Flow arcs layer */}
            {hasFlows && (
              <FlowLayer
                features={countries}
                projection={projection}
                data={flows}
                flowColors={props.flowColors}
                highlightedFlowKey={props.highlightedFlowKey}
                onTooltip={handleTooltip}
              />
            )}

            {/* Routed paths layer — real routes (e.g. from a Sea Routes API), under the vessel dots */}
            {hasRoutes && (
              <RouteLayer
                projection={projection}
                data={routes}
                onTooltip={handleTooltip}
              />
            )}

            {/* Generic markers layer (infrastructure, facilities, etc.) */}
            {hasMarkers && (
              <MarkerLayer
                projection={projection}
                data={markers}
                onTooltip={handleTooltip}
              />
            )}

            {/* Pie/donut glyph markers — category breakdown per point */}
            {hasPieMarkers && (
              <PieMarkerLayer
                projection={projection}
                data={pieMarkers}
                onTooltip={handleTooltip}
              />
            )}

            {/* Vessel markers layer */}
            {hasVessels && (
              <VesselLayer
                projection={projection}
                data={vessels}
                onTooltip={handleTooltip}
              />
            )}
          </g>
        </svg>

        {/* Tooltip — positioned relative to the viewport */}
        {tooltip && tooltip.content && (
          <div
            className="pointer-events-none fixed z-50 rounded-lg border border-border bg-popover px-2.5 py-1.5 text-sm text-popover-foreground shadow-lg"
            style={{
              left: tooltip.x + 16,
              top: tooltip.y - 10,
              maxWidth: 240,
            }}
          >
            {tooltip.content}
          </div>
        )}
      </div>

      {/* Legend section */}
      <MapLegend
        hasChoropleth={hasChoropleth}
        choropleth={choropleth}
        colorScheme={props.colorScheme ?? 'blues'}
        hasFlows={hasFlows}
        flows={flows}
        flowColors={props.flowColors}
        hasMarkers={hasMarkers}
        markers={markers}
        hasVessels={hasVessels}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// MapLegend — color key below the map
// Hooks are called unconditionally; early return moved after all hooks.
// ---------------------------------------------------------------------------

interface MapLegendProps {
  hasChoropleth: boolean;
  choropleth: ChoroplethItem[];
  colorScheme: string;
  hasFlows: boolean;
  flows: FlowItem[];
  flowColors?: Record<string, string>;
  hasMarkers: boolean;
  markers: MarkerItem[];
  hasVessels: boolean;
}

function MapLegend ({
  hasChoropleth,
  choropleth,
  colorScheme,
  hasFlows,
  flows,
  flowColors,
  hasMarkers,
  markers,
  hasVessels,
}: MapLegendProps) {
  // Choropleth gradient legend
  const choroplethLegend = useMemo(() => {
    if (!hasChoropleth || isEmpty(choropleth)) return null;

    const values = map(choropleth, 'value');
    const lo = min(values) ?? 0;
    const hi = max(values) ?? 1;
    const interpolator = get(COLOR_INTERPOLATORS, colorScheme, COLOR_INTERPOLATORS.blues);

    // Sample 10 stops for the gradient
    const stops = Array.from({ length: 10 }, (_, i) => {
      const t = i / 9;
      return { offset: `${t * 100}%`, color: interpolator(t) };
    });

    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{lo.toLocaleString()}</span>
        <svg width={120} height={12}>
          <defs>
            <linearGradient id="choropleth-legend-grad">
              {map(stops, (s, idx) => (
                <stop key={idx} offset={s.offset} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>
          <rect width={120} height={12} rx={2} fill="url(#choropleth-legend-grad)" />
        </svg>
        <span className="text-xs text-muted-foreground">{hi.toLocaleString()}</span>
      </div>
    );
  }, [hasChoropleth, choropleth, colorScheme]);

  // Flow color legend
  const flowLegend = useMemo(() => {
    if (!hasFlows || isEmpty(flows)) return null;

    const groups = [...new Set(map(flows, (d) => d.group ?? 'default'))];
    // Only show legend if there are named groups (not just "default")
    if (groups.length <= 1 && groups[0] === 'default') return null;

    return (
      <div className="flex items-center gap-3">
        {map(groups, (group, i) => {
          const color =
            get(flowColors, group) ?? DEFAULT_FLOW_COLORS[i % DEFAULT_FLOW_COLORS.length];
          return (
            <div key={group} className="flex items-center gap-1">
              <div
                className="h-2 w-4 rounded-sm"
                style={{ backgroundColor: color, opacity: 0.6 }}
              />
              <span className="text-xs text-muted-foreground">{group}</span>
            </div>
          );
        })}
      </div>
    );
  }, [hasFlows, flows, flowColors]);

  // Marker group legend — groups markers by group name and color
  const markerLegend = useMemo(() => {
    if (!hasMarkers || isEmpty(markers)) return null;

    // Deduplicate by group name, keeping the first color per group
    const seen = new Map<string, string>();
    for (const m of markers) {
      const g = m.group ?? 'Marker';
      if (!seen.has(g)) seen.set(g, m.color ?? '#2563eb');
    }
    if (seen.size === 0) return null;

    return (
      <div className="flex items-center gap-3">
        {Array.from(seen.entries(), ([group, color]) => (
          <div key={group} className="flex items-center gap-1">
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs text-muted-foreground">{group}</span>
          </div>
        ))}
      </div>
    );
  }, [hasMarkers, markers]);

  // Vessel status legend
  const vesselLegend = useMemo(() => {
    if (!hasVessels) return null;

    const statuses = [
      { label: 'Under way', color: VESSEL_STATUS_COLORS['under way'] },
      { label: 'At anchor', color: VESSEL_STATUS_COLORS['at anchor'] },
      { label: 'Other', color: VESSEL_STATUS_COLORS.default },
    ];

    return (
      <div className="flex items-center gap-3">
        {map(statuses, (s) => (
          <div key={s.label} className="flex items-center gap-1">
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-xs text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>
    );
  }, [hasVessels]);

  // Early return after all hooks
  const hasLegend = hasChoropleth || hasFlows || hasMarkers || hasVessels;
  if (!hasLegend) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {choroplethLegend}
      {flowLegend}
      {markerLegend}
      {vesselLegend}
    </div>
  );
}
